import React, { FC, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import debounce from "debounce";

import { Input, Label, Button } from "../../common";
import { SUPPORT_EMAIL } from "../../../constants";
import { useChangeUserNameMutation } from "../../../store/services/user";
import { toastSuccess } from "../../../utils/toasts";
import { AccountType } from "../../../types/UserTypes";
import { useTriggerToastOnRTKError } from "../../../hooks/useTriggerToastOnRTKError";

type Inputs = {
  first_name: string;
  last_name: string;
};

type Props = {
  account_type: AccountType;
  first_name: string;
  last_name: string;
  disabled: boolean;
  className?: string;
};

export const ChangeNameForm: FC<Props> = (props) => {
  const {
    account_type = AccountType.CONTRACTOR,
    first_name,
    last_name,
    disabled,
    className = "",
  } = props;

  const {
    watch,
    control,
    formState: { errors },
  } = useForm<Inputs>({
    values: {
      first_name,
      last_name,
    },
  });

  const [changeUserName, { isSuccess, isError, error }] =
    useChangeUserNameMutation();

  useTriggerToastOnRTKError(isError, error);

  useEffect(() => {
    const subscription = watch(
      debounce(async (newValues: any, { name }: any) => {
        if (name) {
          await changeUserName(newValues);
        }
      }, 1000)
    );

    return () => subscription.unsubscribe();
  }, [watch, changeUserName]);

  useEffect(() => {
    if (isSuccess) {
      toastSuccess("Your name updated");
    }
  }, [isSuccess]);

  return (
    <div className={className}>
      {account_type === AccountType.BUSINESS && (
        <div className="pt-9 pb-6 uppercase text-sm font-manrope font-semibold text-gray-300 tracking-[1px] border-b border-gray-50 mb-9">
          Representative
        </div>
      )}

      <form>
        <Label label="First name" className="mb-8">
          <Controller
            name="first_name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                type="text"
                disabled={disabled}
                {...field}
                error={errors.first_name && "Error"}
              />
            )}
          />
        </Label>

        <Label label="Last name" className="mb-8">
          <Controller
            name="last_name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                type="text"
                disabled={disabled}
                {...field}
                error={errors.last_name && "Error"}
              />
            )}
          />
        </Label>

        {disabled && (
          <div className="flex flex-row items-center justify-between">
            <div className="text-base max-w-[285px] text-gray-300">
              To change name please contact customer support
            </div>

            <Button
              type="submit"
              variant={Button.variant.PRIMARY}
              component={NavLink}
              to={`mailto:${SUPPORT_EMAIL}`}
            >
              Get help
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};
